import { isNativeClientUserAgent, isNativeShellMode } from './nativeClientEnv';

export type PushBlockReason =
  | 'native_client'
  | 'desktop'
  | 'tablet'
  | 'missing_apis'
  | 'insecure_context'
  | 'ios_requires_pwa';

export interface PushEnvironment {
  userAgent: string;
  isNativeShell: boolean;
  isStandalone: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  supportsServiceWorker: boolean;
  supportsPushManager: boolean;
  supportsNotifications: boolean;
  isSecureContext: boolean;
  canUsePush: boolean;
  pushMode: 'pwa' | 'browser' | 'unsupported';
  blockReason?: PushBlockReason;
}

function isLocalDevelopmentHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

export function getPushEnvironment(): PushEnvironment {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      userAgent: '',
      isNativeShell: false,
      isStandalone: false,
      isIOS: false,
      isAndroid: false,
      isPhone: false,
      isTablet: false,
      isDesktop: true,
      supportsServiceWorker: false,
      supportsPushManager: false,
      supportsNotifications: false,
      isSecureContext: false,
      canUsePush: false,
      pushMode: 'unsupported',
      blockReason: 'missing_apis',
    };
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isNativeShell = isNativeShellMode() || isNativeClientUserAgent();
  const isIOS = /iphone/.test(userAgent);
  const isIPad =
    /ipad/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(userAgent);
  const isAndroidPhone = isAndroid && /mobile/.test(userAgent);
  const isAndroidTablet = isAndroid && !isAndroidPhone;
  const isTablet = isIPad || /tablet/.test(userAgent) || isAndroidTablet;
  const isPhone = isIOS || isAndroidPhone;
  const isDesktop = !isPhone && !isTablet;
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const supportsServiceWorker = 'serviceWorker' in navigator;
  const supportsPushManager = 'PushManager' in window;
  const supportsNotifications = 'Notification' in window;
  const secureContext =
    window.isSecureContext || isLocalDevelopmentHost(window.location.hostname);

  let blockReason: PushBlockReason | undefined;
  let pushMode: PushEnvironment['pushMode'] = 'unsupported';
  let canUsePush = false;

  if (isNativeShell) {
    blockReason = 'native_client';
  } else if (isDesktop) {
    blockReason = 'desktop';
  } else if (isTablet) {
    blockReason = 'tablet';
  } else if (!secureContext) {
    blockReason = 'insecure_context';
  } else if (!supportsServiceWorker || !supportsPushManager || !supportsNotifications) {
    blockReason = 'missing_apis';
  } else if (isIOS && !isStandalone) {
    blockReason = 'ios_requires_pwa';
  } else {
    canUsePush = true;
    pushMode = isStandalone || isIOS ? 'pwa' : 'browser';
  }

  return {
    userAgent,
    isNativeShell,
    isStandalone,
    isIOS,
    isAndroid,
    isPhone,
    isTablet,
    isDesktop,
    supportsServiceWorker,
    supportsPushManager,
    supportsNotifications,
    isSecureContext: secureContext,
    canUsePush,
    pushMode,
    blockReason,
  };
}

export function shouldRegisterBrowserServiceWorker(): boolean {
  return !getPushEnvironment().isNativeShell;
}

export function isBackgroundSensitiveMobileClient(): boolean {
  const environment = getPushEnvironment();
  return environment.isPhone && !environment.isNativeShell;
}

export function getSocketTransportsForCurrentClient(): Array<'websocket' | 'polling'> {
  return isBackgroundSensitiveMobileClient() ? ['websocket'] : ['websocket', 'polling'];
}

export function getPushSupportMessage(environment: PushEnvironment = getPushEnvironment()): string {
  switch (environment.blockReason) {
    case 'native_client':
      return 'Push-Benachrichtigungen werden in Native-Clients nicht ueber den Browser verwaltet.';
    case 'desktop':
      return 'Push-Benachrichtigungen sind in dieser App nur auf Smartphones vorgesehen.';
    case 'tablet':
      return 'Push-Benachrichtigungen sind fuer Tablets in dieser App deaktiviert.';
    case 'missing_apis':
      return 'Dieser Browser unterstuetzt die benoetigten Web-Push-APIs nicht vollstaendig.';
    case 'insecure_context':
      return 'Web-Push funktioniert nur ueber HTTPS oder auf localhost.';
    case 'ios_requires_pwa':
      return 'Auf iPhones funktionieren Push-Benachrichtigungen nur in der installierten PWA.';
    default:
      return environment.pushMode === 'pwa'
        ? 'Push-Benachrichtigungen sind in dieser installierten PWA verfuegbar.'
        : 'Push-Benachrichtigungen sind in diesem mobilen Browser verfuegbar.';
  }
}
